// src/oura/components/aeo/PrescreenCard.tsx
//
// AC: "Prescreen view: submit {domain, business_name, anchors} → show
// verdict; INVISIBLE → CTA to run the full assessment." (#3065)
//
// A quick, free-standing qualifier on the AEO Assessment screen — separate
// from the New Scan modal on the Businesses page, which (product decision
// 23 Jul, see NewScanModal.tsx) intentionally keeps its own prescreen
// backend-internal. This card is the ticket's explicit user-facing prescreen
// entry point; it doesn't touch that modal's flow.

import { useState } from "react";
import { Loader2Icon, SearchCheckIcon, ArrowRightIcon, CheckCircle2Icon, AlertTriangleIcon, XCircleIcon } from "lucide-react";
import { Card, CardBody, CardHeader } from "../../primitives/Card";
import { Button } from "../../primitives/Button";
import { Chip } from "../../primitives/Chip";
import { cn } from "../../lib/cn";
import { prescreen, type AeoPrescreenResponse, type AeoPrescreenVerdict } from "@service/aeo";

const INPUT =
    "h-9 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-blue-500";

const VERDICT_META: Record<
    AeoPrescreenVerdict,
    { label: string; chip: string; iconColor: string; icon: typeof CheckCircle2Icon }
> = {
    VISIBLE: { label: "Visible", chip: "border-emerald-200 bg-emerald-50 text-emerald-700", iconColor: "text-emerald-600", icon: CheckCircle2Icon },
    PARTIAL: { label: "Partially visible", chip: "border-amber-200 bg-amber-50 text-amber-700", iconColor: "text-amber-600", icon: AlertTriangleIcon },
    INVISIBLE: { label: "Invisible", chip: "border-rose-200 bg-rose-50 text-rose-700", iconColor: "text-rose-600", icon: XCircleIcon },
};

export interface PrescreenPrefill {
    domain: string;
    business_name: string;
    anchors_primary: string;
}

export function PrescreenCard({
    onRunFull,
}: {
    /** INVISIBLE CTA — hands the typed fields up so the create form can prefill. */
    onRunFull: (prefill: PrescreenPrefill) => void;
}) {
    const [domain, setDomain] = useState("");
    const [businessName, setBusinessName] = useState("");
    const [anchors, setAnchors] = useState("");
    const [touched, setTouched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<AeoPrescreenResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    const valid = domain.trim().length > 0 && businessName.trim().length > 0;

    const run = async () => {
        setTouched(true);
        if (!valid) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await prescreen({
                domain: domain.trim(),
                business_name: businessName.trim(),
                anchors: {
                    primary: anchors.split(",").map((a) => a.trim()).filter(Boolean),
                },
            });
            setResult(res);
        } catch {
            // All engines down → 502 per #3065.
            setError("Both engines were unreachable for this check. Try again in a moment.");
        } finally {
            setLoading(false);
        }
    };

    const meta = result ? VERDICT_META[result.verdict] : null;
    const VerdictIcon = meta?.icon;

    return (
        <Card>
            <CardHeader
                title="Quick check"
                description="A fast, free read on whether AI engines already name this business — before committing to a full paid assessment."
                icon={SearchCheckIcon}
            />
            <CardBody className="pt-2">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <input
                        className={cn(INPUT, "font-mono", touched && !domain.trim() && "border-rose-400")}
                        placeholder="dentalhome.com.my"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                    />
                    <input
                        className={cn(INPUT, touched && !businessName.trim() && "border-rose-400")}
                        placeholder="Business name"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                    />
                    <input
                        className={INPUT}
                        placeholder="Anchor areas (optional), e.g. Mont Kiara"
                        value={anchors}
                        onChange={(e) => setAnchors(e.target.value)}
                    />
                </div>
                <div className="mt-3 flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                        {touched && !valid ? "Domain and business name are required." : "Takes about 5–15 seconds."}
                    </p>
                    <Button size="sm" variant="outline" disabled={loading} onClick={() => void run()}>
                        {loading ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <SearchCheckIcon className="h-4 w-4" />}
                        Check visibility
                    </Button>
                </div>

                {error && (
                    <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
                        {error}
                    </div>
                )}

                {result && meta && VerdictIcon && (
                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                                <VerdictIcon className={cn("mt-0.5 h-5 w-5 flex-shrink-0", meta.iconColor)} />
                                <div>
                                    <div className="flex items-center gap-2">
                                        <Chip className={meta.chip}>{meta.label}</Chip>
                                        <span className="text-xs text-gray-500">
                                            checked {result.engines_checked.join(" + ")}
                                        </span>
                                    </div>
                                    <p className="mt-1.5 text-sm text-gray-700">{result.notes}</p>
                                </div>
                            </div>
                        </div>
                        {result.verdict === "INVISIBLE" && (
                            <div className="mt-3 flex justify-end">
                                <Button
                                    size="sm"
                                    onClick={() =>
                                        onRunFull({
                                            domain: domain.trim(),
                                            business_name: businessName.trim(),
                                            anchors_primary: anchors,
                                        })
                                    }
                                >
                                    Run full assessment
                                    <ArrowRightIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </CardBody>
        </Card>
    );
}
